import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Plus, Trash2, Loader2 } from 'lucide-react';

interface Education {
  id: string;
  institution_name: string;
  degree?: string | null;
  field_of_study?: string | null;
  grade?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface EditableEducationProps {
  candidateId: string;
  education: Education[];
  onUpdate: () => void;
}

const EditableEducation = ({ candidateId, education, onUpdate }: EditableEducationProps) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEdu, setSelectedEdu] = useState<Education | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    institution_name: '',
    degree: '',
    field_of_study: '',
    grade: '',
    start_date: '',
    end_date: '',
  });

  const openAdd = () => {
    setSelectedEdu(null);
    setFormData({
      institution_name: '',
      degree: '',
      field_of_study: '',
      grade: '',
      start_date: '',
      end_date: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (edu: Education) => {
    setSelectedEdu(edu);
    setFormData({
      institution_name: edu.institution_name || '',
      degree: edu.degree || '',
      field_of_study: edu.field_of_study || '',
      grade: edu.grade || '',
      start_date: edu.start_date || '',
      end_date: edu.end_date || '',
    });
    setDialogOpen(true);
  };

  const openDelete = (edu: Education) => {
    setSelectedEdu(edu);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.institution_name) {
      toast({ title: 'Institution name is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (selectedEdu) {
        const { error } = await supabase
          .from('candidate_education')
          .update(formData)
          .eq('id', selectedEdu.id);
        if (error) throw error;
        toast({ title: 'Education updated' });
      } else {
        const { error } = await supabase
          .from('candidate_education')
          .insert({ ...formData, candidate_id: candidateId });
        if (error) throw error;
        toast({ title: 'Education added' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEdu) return;
    
    try {
      const { error } = await supabase
        .from('candidate_education')
        .delete()
        .eq('id', selectedEdu.id);
      if (error) throw error;
      toast({ title: 'Education deleted' });
      setDeleteDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Education
        </Button>
      </div>

      {education.length > 0 ? (
        education.map((edu) => (
          <Card key={edu.id} className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{edu.institution_name}</h4>
                <p className="text-sm text-muted-foreground">{edu.degree}</p>
                {edu.field_of_study && (
                  <p className="text-sm text-muted-foreground">{edu.field_of_study}</p>
                )}
                {edu.grade && (
                  <p className="text-sm text-muted-foreground">Grade: {edu.grade}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {edu.start_date} - {edu.end_date || 'Present'}
                </p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(edu)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openDelete(edu)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </Card>
        ))
      ) : (
        <p className="text-center text-muted-foreground py-8">No education records</p>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEdu ? 'Edit Education' : 'Add Education'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Institution Name *</Label>
              <Input 
                value={formData.institution_name}
                onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Degree</Label>
              <Input 
                value={formData.degree}
                onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
              />
            </div>
            <div>
              <Label>Field of Study</Label>
              <Input 
                value={formData.field_of_study}
                onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
              />
            </div>
            <div>
              <Label>Grade</Label>
              <Input 
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Education</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this education record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditableEducation;
